import React from 'react';

const CreditNote: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Credit Notes</h1>
        <p className="text-gray-600">
            A Credit Note is a document issued by a vendor to a customer when goods are returned to
            the vendor after a sale. This can happen due to reasons like the goods being damaged or not satisfactory.
            The credit note is created for the value of the goods returned.
        </p>
      </div>

      {/* Bills Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Credit Notes Overview</h2>
        <p className="text-gray-600 mb-4">
          Credit Notes in DAS represent customer returns goods or purchase orders are returned to the vendor.

        </p>

        <div className="mb-6">
          <img
            src="/images/sales/credit-notes/list.png"
            alt="Bills List View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Features</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Direct credit notes creation and management</li>
              <li>Purchase order to bill conversion</li>
              <li>Credit Notes recording and tracking</li>
              <li>Sale Person credit application</li>
              <li>Bill editing and void functionality</li>
              <li>Real Time Search and Filtering</li>
              <li>PDF generation and document management</li>
              <li>Journal entry automation</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Status Types</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Draft:</strong> Credit Notes being prepared</li>
              <li><strong>Confirmed:</strong> Credit Notes finalized and ready for payment</li>
              <li><strong>Partial Paid:</strong> Partially paid bill</li>
              <li><strong>Paid:</strong> Fully paid payment</li>
              <li><strong>Void:</strong> Cancelled payment</li>
            </ul>
          </div>
        </div>
      </div>


      {/* Search and Filtering */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Search and Filtering Bills</h2>
        <p className="text-gray-600 mb-4">
          Efficiently locate and manage credit notes using comprehensive search and filtering options with multiple criteria.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/credit-notes/search.png"
            alt="Credit Notes Search Dialog"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Advanced Search Dialog</h3>
        <p className="text-gray-600 mb-4">
          Use the comprehensive search dialog to find specific bills with multiple filter criteria:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Identification Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Credit Notes #:</strong> Search by specific bill number</li>
              <li><strong>Reference #:</strong> Find by supplier's invoice reference</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Date and Entity Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Date Range:</strong> Filter by credit notes creation dates</li>
              <li><strong>Customer:</strong> Search by customer with lookup function</li>
              <li><strong>Warehouse:</strong> Filter by delivery warehouse</li>
            </ul>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Status and Branch Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Status:</strong> Filter by Draft, Confirmed, Partial Paid, Paid, Void</li>
              <li><strong>Branch:</strong> Filter by organizational branch</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Search Benefits:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Quickly locate bills for payment processing</li>
              <li>Find bills by supplier for vendor management</li>
              <li>Filter by date ranges for reporting periods</li>
              <li>Search by batch numbers for inventory tracking</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Quick Search with Search Icon</h3>
        <p className="text-gray-600 mb-4">
          Use the search icon (🔍) in the top-right corner for instant bill filtering:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Real-time Search:</strong> Filter bills as you type</li>
          <li><strong>Multi-field Search:</strong> Searches across bill numbers, suppliers, and references</li>
          <li><strong>Quick Access:</strong> No separate forms needed for basic searches</li>
          <li><strong>Immediate Results:</strong> Instant table filtering and updates</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Search Use Cases</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Payment Management:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Find overdue bills by due date range</li>
              <li>Locate bills ready for payment processing</li>
              <li>Search for specific supplier bills</li>
              <li>Filter by payment status</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Reporting and Audit:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Generate reports for specific date ranges</li>
              <li>Find credit notes for audit purposes</li>
              <li>Track credit notes by warehouse or branch</li>
              <li>Verify customer order to sale person conversions</li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Search Tip:</strong> Use the advanced search dialog for complex queries with multiple criteria,
            and the quick search icon for simple, immediate filtering. Combine different search fields to narrow
            down results and find exactly the bills you need for efficient accounts payable management.
          </p>
        </div>
      </div>

      {/* Creating and Editing Bills */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating and Editing Credit Notes</h2>
        <p className="text-gray-600 mb-4">
          Credit Notes can be created directly (when allowed) or generated from purchase orders. The credit notes creation
          process includes detailed product information, batch tracking, and financial calculations.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/credit-notes/edit-form.png"
            alt="Edit Bill Form"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Credit Notes Header Information</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Basic Details:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Customer:</strong> Select vendor with search functionality</li>
              <li><strong>Branch:</strong> Organizational branch assignment</li>
              <li><strong>Purchase Order #:</strong> Link to originating PO</li>
              <li><strong>Bill Date:</strong> Date of bill creation</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Additional Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Reference #:</strong> Supplier's invoice reference</li>
              <li><strong>Payment Terms:</strong> Payment conditions</li>
              <li><strong>Currency:</strong> Transaction currency</li>
              <li><strong>Warehouse:</strong> Delivery location</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Product Details and Batch Tracking</h3>
        <p className="text-gray-600 mb-4">
          Bills support detailed product information with advanced batch tracking capabilities:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Product Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Product Details:</strong> Select items from inventory</li>
              <li><strong>Account:</strong> Chart of accounts classification</li>
              <li><strong>Quantity:</strong> Received quantity</li>
              <li><strong>Rate:</strong> Unit price</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Batch Management:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Batch Number:</strong> Lot identification</li>
              <li><strong>Stock on Hand:</strong> Current inventory levels</li>
              <li><strong>Batch Expiry Date:</strong> Expiration tracking</li>
              <li><strong>Auto-generated Batches:</strong> System-created batch numbers</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Financial Calculations</h3>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Discount Management:</strong> Percentage or fixed amount discounts</li>
          <li><strong>Tax Calculations:</strong> Automatic tax computation</li>
          <li><strong>Sub Total and Total:</strong> Automatic amount calculations</li>
          <li><strong>Currency Handling:</strong> Multi-currency support</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Notes and Attachments</h3>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Notes Section:</strong> Additional bill information and comments</li>
          <li><strong>File Attachments:</strong> Upload supporting documents</li>
          <li><strong>File Size Limits:</strong> Maximum file size restrictions</li>
          <li><strong>Document Management:</strong> Organized file storage</li>
        </ul>
      </div>

      {/* Credit-Notes Detail View and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Bill Detail View and Management</h2>
        <p className="text-gray-600 mb-4">
          The bill detail view provides comprehensive information and access to all bill management functions.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/credit-notes/detail.png"
            alt="Bill Detail View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Action Buttons and Functions</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Primary Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Edit:</strong> Modify bill details (when allowed)</li>
              <li><strong>PDF/Print:</strong> Generate bill documents</li>
              <li><strong>Record Payment:</strong> Process payment against bill</li>
              <li><strong>Apply Credits:</strong> Use supplier credits</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>More Actions Menu:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Clone:</strong> Create similar bills</li>
              <li><strong>Confirm Bill:</strong> Finalize draft bills</li>
              <li><strong>Void Bill:</strong> Cancel bills</li>
              <li><strong>Delete:</strong> Remove bills</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Bill Information Display</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Header Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Bill number and date</li>
              <li>Due date and payment terms</li>
              <li>Batch number and PO reference</li>
              <li>Supplier details and contact information</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Financial Summary:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Sub total and tax amounts</li>
              <li>Total bill amount</li>
              <li>Remaining balance due</li>
              <li>Payment history and credits applied</li>
            </ul>
          </div>
        </div>
      </div>



      {/* Credit-Notes Tracking and Integration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Credit-Notes Tracking and Integration</h2>
        <p className="text-gray-600 mb-4">
          Track bill relationships with payments, purchase orders, and view detailed transaction history.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Payment and Purchase Order Tabs</h3>
        <div className="mb-6">
          <img
            src="/images/sales/credit-notes/status.png"
            alt="Payments Made Tab"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Payments Made Tab:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Complete payment history for the bill</li>
              <li>Payment dates and reference numbers</li>
              <li>Payment modes and amounts</li>
              <li>Remaining balance tracking</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Purchase Order Tab:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Link to originating purchase order</li>
              <li>Purchase order details and status</li>
              <li>Order-to-bill conversion tracking</li>
              <li>Procurement audit trail</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Credits Notes</h3>
        <div className="mb-6">
          <img
             src="/images/sales/credit-notes/note.png"
            alt="Credits Applied Tab"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <p className="text-gray-600 mb-4">
          Monitor all customer credits applied to creadit-notes:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Branch:</strong>Branch of Company</li>
          <li><strong>Date</strong> Creating Date </li>
          <li><strong>Paid Via</strong>Choose Cash or Bank</li>
          <li><strong>Reference:</strong> </li>
          <li><strong>Amount:</strong>Cost of Goods </li>
          <li><strong>From Account:</strong>Choose Bank Account</li>
          <li><strong>Description:</strong>Add Notes for Payment</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Journal Entry Integration</h3>
        <div className="mb-6">
          <img
            src="/images/sales/credit-notes/journal.png"
            alt="Journal Entries"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <p className="text-gray-600 mb-4">
          View automatic journal entries generated from bill transactions:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Account Entries:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Accounts Payable:</strong> Credit entries for amounts owed</li>
              <li><strong>Tax Payable:</strong> Tax liability entries</li>
              <li><strong>Inventory Asset:</strong> Debit entries for received goods</li>
              <li><strong>Expense Accounts:</strong> Direct expense postings</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Journal Features:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Automatic posting upon bill confirmation</li>
              <li>Debit and credit balance verification</li>
              <li>Multi-currency support and conversion</li>
              <li>Audit trail for all journal postings</li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Integration Note:</strong> Bills automatically generate journal entries that update
            accounts payable, inventory, and expense accounts. This ensures accurate financial reporting
            and maintains proper accounting records for all supplier transactions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreditNote;
